<!-- MIT License

Copyright (c) 2025 Brad Schick

Permission is hereby granted, free of charge, to any person obtaining a copy
of this software and associated documentation files (the "Software"), to deal
in the Software without restriction, including without limitation the rights
to use, copy, modify, merge, publish, distribute, sublicense, and/or sell
copies of the Software, and to permit persons to whom the Software is
furnished to do so, subject to the following conditions:

The above copyright notice and this permission notice shall be included in all
copies or substantial portions of the Software.

THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR
IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY,
FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE
AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER
LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM,
OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN THE
SOFTWARE. -->
<mat-accordion multi>
  <mat-expansion-panel matRipple [matRippleDisabled]="true">
    <mat-expansion-panel-header>
      <mat-panel-title class="panel-title">{{(loopsInput() || 1) > 1 ? 'Encryption Modes' : 'Encryption Mode'}}</mat-panel-title>
    </mat-expansion-panel-header>
    <div class="algs-area">
      <app-algorithms [modes]="algorithmList()" [count]="loopCount()" (modesChange)="onModesChange($event)"></app-algorithms>
      <a [routerLink]="'/help/faqs'" [queryParams]="{search: 'which cipher'}" class="alg-help" target="_blank">
        <mat-icon class="help-icon">help</mat-icon>
      </a>
    </div>
  </mat-expansion-panel>
  <mat-expansion-panel (afterExpand)="cipherPanelExpanded.set(true)" (closed)="cipherPanelExpanded.set(false)" matRipple
    [matRippleDisabled]="true">
    <mat-expansion-panel-header>
      <mat-panel-title class="panel-title">Advanced Options</mat-panel-title>
    </mat-expansion-panel-header>

    <div class="mtop0">
      <p class="options-header1">
        <span>Password Handling</span>
        <a [routerLink]="'/help/faqs'" [queryParams]="{search: 'password handling'}" target="_blank">
          <mat-icon class="help-icon">help</mat-icon>
        </a>
      </p>
      <div class="options-flex">
        <div>
          <mat-form-field subscriptSizing="dynamic">
            <mat-label>Hash Iterations</mat-label>
            <input type="number" [min]="ICOUNT_MIN" [max]="icountMax()" step="200000" matInput [(ngModel)]="icountInput"
              (blur)="onBlurICount()"
              [matTooltip]="'Larger values make password guessing slower. Range ['+ICOUNT_MIN/1000+'K,'+icountMax()/1000000+'M]'" />
          </mat-form-field>
        </div>
        <div>
          <mat-form-field subscriptSizing="dynamic">
            <mat-label #minStrLabel>Default Strength</mat-label>
            <mat-select id="pwdStrength" [(ngModel)]="strengthSelect" (ngModelChange)="onPwdStrengthChange($event)"
              matTooltip="Defaul minimum password strength allowed for encryption">
              <mat-option class="red-pwd-strength" value="0">Terrible</mat-option>
              <mat-option class="red-pwd-strength" value="1">Weak</mat-option>
              <mat-option class="yellow-pwd-strength" value="2">Decent</mat-option>
              <mat-option class="green-pwd-strength" value="3">Good</mat-option>
              <mat-option class="green-pwd-strength" value="4">Strong</mat-option>
            </mat-select>
          </mat-form-field>
        </div>
        <div>
          <mat-slide-toggle [(ngModel)]="checkPwnedToggle" (ngModelChange)="onCheckPwnedChange($event)"
            matTooltip="Check if your password matches a known stolen password">Check If Stolen
          </mat-slide-toggle>
        </div>
      </div>
      <span class="hash-warning" [style.visibility]="cipherPanelExpanded() ? 'visible' : 'hidden'">
        {{hashTimeWarning()}}
      </span>
    </div>

    <div class="mtop10">
      <p class="options-header3">
        <span>Display Privacy</span>
        <a [routerLink]="'/help/faqs'" [queryParams]="{search: 'display privacy'}" target="_blank">
          <mat-icon class="help-icon">help</mat-icon>
        </a>
      </p>
      <div class="options-flex">
        <div>
          <mat-form-field subscriptSizing="dynamic">
            <mat-label>Cache Time (secs)</mat-label>
            <input type="number" min="0" [max]="ACTIVITY_TIMEOUT" step="10" matInput [(ngModel)]="cacheTimeInput"
            (ngModelChange)="onCacheTimeChange($event)"
            (blur)="onBlurCacheTime()"
            matTooltip="Number of seconds until your clear text and cached password are automatically flushed" />
          </mat-form-field>
        </div>
        <div>
          <mat-slide-toggle [(ngModel)]="visClearToggle" (ngModelChange)="onVisClearChnage($event)"
            matTooltip="Automatically flush clear text and cached password on focus change">Clear When Hidden
          </mat-slide-toggle>
        </div>
        <div>
          <mat-slide-toggle [(ngModel)]="hidePwdToggle" (ngModelChange)="onHidePwdChange($event)"
            matTooltip="Hide encryption passwords while typing">Hide Passwords<br />
          </mat-slide-toggle>
        </div>
      </div>
    </div>

    <div class="mtop10">
      <p class="options-header3">
        <span>Other Options</span>
        <a [routerLink]="'/help/faqs'" [queryParams]="{search: 'cipher armor formats, loops, reminder'}"
          id="format-link" target="_blank">
          <mat-icon class="help-icon">help</mat-icon>
        </a>
      </p>
      <div class="options-flex">
        <div>
          <mat-form-field subscriptSizing="dynamic">
            <mat-label>Loop Encrypt</mat-label>
            <input type="number" min="1" [max]="LOOPS_MAX" step="1" matInput [(ngModel)]="loopsInput"
              (blur)="onBlurLoops()" name="loops"
              matTooltip="Number of times to run encryption in a loop. Enter a different password each loop" />
          </mat-form-field>
        </div>
        <div>
          <mat-form-field subscriptSizing="dynamic">
            <mat-label #formatLabel>Cipher Armor Format</mat-label>
            <mat-select id="format" [(ngModel)]="formatSelect" (ngModelChange)="onFormatChange($event)"
              matTooltip="Format of output cipher armor. Only use 'link' when you are not concerned about others changing the cipher armor">
              <mat-option value="compact">Compact</mat-option>
              <mat-option value="indent">Indent</mat-option>
              <mat-option value="link">Link</mat-option>
            </mat-select>
          </mat-form-field>
        </div>
        <div>
          <mat-slide-toggle [(ngModel)]="reminderToggle" (ngModelChange)="onReminderChange($event)"
            [disabled]="reminderLocked()"
            matTooltip="Add a reminder to cipher armor that Quick Crypt was used">Decryption
            Reminder
          </mat-slide-toggle>
        </div>
      </div>
    </div>

      <div class="mtop35">
        <button mat-raised-button (click)="onClickResetOptions()"
          matTooltip="Reset all options to default values">
          Reset To Defaults
        </button>
      </div>
  </mat-expansion-panel>
</mat-accordion>